// Wizard /plans/new — 3 passos com useReducer (sem persist).
// Story 2.5b.

import { useReducer, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { CreatePlanDto, CreatePlanInput } from '@control-center/shared';
import { createPlanSchema } from '@control-center/shared';

import { Step1Data } from './Step1Data';
import { Step2Entitlements } from './Step2Entitlements';
import { Step3Review } from './Step3Review';
import { useCreatePlan } from '../../../hooks/use-plans';
import { useExternalPlans } from '../../../hooks/use-external-plans';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { extractErrorMessage } from '../../../lib/errors';

type Step = 1 | 2 | 3;

interface WizardState {
  step: Step;
  data: CreatePlanInput;
}

type WizardAction =
  | { type: 'SET_STEP'; step: Step }
  | { type: 'PATCH_DATA'; patch: Partial<CreatePlanInput> }
  | { type: 'RESET' };

const EMPTY_DATA: CreatePlanInput = {
  slug: '',
  name: '',
  description: '',
  type: 'single_saas',
  monthly_price: 0,
  annual_price: null,
  setup_fee: 0,
  promo_price: null,
  billing_cycle: 'mensal',
  discount_annual_pct: null,
  trial_days: 0,
  requires_card_for_trial: false,
  blocks_repeat_trial: true,
  limits: {},
  features: {},
  checkout_highlights: [],
  status: 'ativo',
  auto_emit_nf: false,
  service_description: null,
  entitlements: [],
};

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'PATCH_DATA':
      return { ...state, data: { ...state.data, ...action.patch } };
    case 'RESET':
      return { step: 1, data: EMPTY_DATA };
    default:
      return state;
  }
}

export function PlansWizardPage(): JSX.Element {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, { step: 1, data: EMPTY_DATA });
  const create = useCreatePlan();

  const saasList = useSaasProducts({ limit: 100 });
  const externalsList = useExternalPlans({ limit: 100 });
  const saasProducts = saasList.data?.items ?? [];
  const externalPlans = externalsList.data?.items ?? [];

  const stepLabels = useMemo(
    () => [
      { n: 1, label: 'Dados' },
      { n: 2, label: 'Entitlements' },
      { n: 3, label: 'Revisão' },
    ],
    [],
  );

  function cancel(): void {
    if (window.confirm('Cancelar criação do plano? Dados serão perdidos.')) {
      navigate('/plans');
    }
  }

  async function submit(): Promise<void> {
    try {
      const parsed: CreatePlanDto = createPlanSchema.parse(state.data);
      await create.mutateAsync(parsed);
      toast.success('Plano criado.');
      navigate('/plans');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar o plano.'));
    }
  }

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Novo plano</h1>
        <p className="text-[13px] text-muted">Wizard guiado em 3 passos.</p>
      </header>

      {/* Stepper */}
      <ol className="flex items-center gap-2">
        {stepLabels.map(({ n, label }, idx) => {
          const isCurrent = state.step === n;
          const isDone = state.step > n;
          return (
            <li key={n} className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold transition-colors ${
                  isDone
                    ? 'bg-good text-page'
                    : isCurrent
                      ? 'bg-ink text-page'
                      : 'bg-panel-3 text-muted-2'
                }`}
              >
                {n}
              </span>
              <span className={`text-[13px] ${isCurrent ? 'font-medium text-ink' : 'text-muted'}`}>
                {label}
              </span>
              {idx < stepLabels.length - 1 && <span className="mx-2 h-px w-12 bg-line" />}
            </li>
          );
        })}
      </ol>

      <div className="rounded-[14px] border border-line bg-panel p-6">
        {state.step === 1 && (
          <Step1Data
            data={state.data}
            onPatch={(patch) => dispatch({ type: 'PATCH_DATA', patch })}
            onNext={() => dispatch({ type: 'SET_STEP', step: 2 })}
            onCancel={cancel}
          />
        )}
        {state.step === 2 && (
          <Step2Entitlements
            data={state.data}
            saasProducts={saasProducts}
            externalPlans={externalPlans}
            onPatch={(patch) => dispatch({ type: 'PATCH_DATA', patch })}
            onBack={() => dispatch({ type: 'SET_STEP', step: 1 })}
            onNext={() => dispatch({ type: 'SET_STEP', step: 3 })}
            onCancel={cancel}
          />
        )}
        {state.step === 3 && (
          <Step3Review
            data={state.data}
            saasProducts={saasProducts}
            externalPlans={externalPlans}
            isSubmitting={create.isPending}
            onBack={() => dispatch({ type: 'SET_STEP', step: 2 })}
            onSubmit={submit}
            onCancel={cancel}
          />
        )}
      </div>
    </div>
  );
}
